// src/components/ProfessionalsShowcase.tsx
// Carrusel de prueba social: profesionales que YA pagan/usan ConsultAR.
// Marquee con auto-scroll, pausa al hover, respeta prefers-reduced-motion.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface ShowcasePro {
  id: number;
  nombre: string;
  apellido: string;
  business_name: string | null;
  especialidad: string | null;
  professional_type: string;
  picture: string | null;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getInitials = (p: ShowcasePro) => {
  const base = p.business_name || `${p.nombre} ${p.apellido}`;
  return base
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');
};

const ProCard: React.FC<{ p: ShowcasePro }> = ({ p }) => {
  const name = p.business_name || `${p.nombre} ${p.apellido}`.trim();
  return (
    <Link
      to={`/p/${p.id}`}
      className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl px-4 py-3 mx-2 hover:bg-white/20 hover:border-white/30 transition-colors w-[250px] flex-shrink-0"
    >
      {p.picture ? (
        <img src={p.picture} alt={name} className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-cyan-400 to-green-400 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {getInitials(p)}
        </div>
      )}
      <div className="min-w-0 text-left">
        <p className="text-white font-semibold text-sm truncate">{name}</p>
        {p.especialidad && <p className="text-cyan-300 text-xs truncate">{p.especialidad}</p>}
      </div>
    </Link>
  );
};

const ProfessionalsShowcase: React.FC = () => {
  const [pros, setPros] = useState<ShowcasePro[]>([]);
  // Si el usuario/SO tiene "reducir movimiento", no animamos el marquee y
  // mostramos las tarjetas ÚNICAS en una grilla centrada (sin duplicados apilados).
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/professionals/showcase`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.success && Array.isArray(data.professionals)) setPros(data.professionals);
      })
      .catch(() => { /* sin vitrina, no rompe el hero */ });
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  if (pros.length === 0) return null;

  // Rellenamos hasta tener suficientes cards para llenar el ancho, y duplicamos
  // en dos mitades idénticas para que el loop (translateX -50%) sea sin cortes.
  let filled: ShowcasePro[] = [];
  while (filled.length < 8) filled = filled.concat(pros);
  const loop = [...filled, ...filled];

  // Con reduce-motion: tarjetas únicas (sin loop) para que la grilla no muestre repetidos.
  const items = reducedMotion ? pros : loop;

  return (
    <div className="mb-10 sm:mb-12">
      <p className="text-center text-sm font-semibold uppercase tracking-widest text-gray-400 mb-5">
        Ellos ya lo usan
      </p>
      <div
        className={
          reducedMotion
            ? 'relative max-w-6xl mx-auto px-2'
            : 'marquee-mask relative overflow-hidden max-w-5xl mx-auto'
        }
        style={reducedMotion ? undefined : { WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
                    maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' }}>
        <div className="marquee-track py-1">
          {items.map((p, i) => (
            <ProCard key={`${p.id}-${i}`} p={p} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProfessionalsShowcase;
